import { useCallback, useState } from 'react';
import { Roles } from 'enums';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import { LicenseShort, User } from '../account-management.types';
import { License } from 'types/licenses.types';

/**
 * Manages all user-related dialog states in account management
 */

interface CreateAndEditUserDialogState {
  open: boolean;
  loading: boolean;
  user: User | null;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  language: string;
  emailInvalid: boolean;
  emailAlreadyExists: boolean;
  firstNameInvalid: boolean;
  lastNameInvalid: boolean;
  invitingUserInvalid: boolean;
}

interface AddLicenseToUserDialogState {
  open: boolean;
  loading: boolean;
  licensesLoading: boolean;
  accountId: string | null;
  tenantId: string | null;
  allLicenses: License[];
  selectedLicenses: string[];
}

interface ManageUserLicensesDialogState {
  open: boolean;
  loading: boolean;
  licensesLoading: boolean;
  accountId: string | null;
  tenantId: string | null;
  selectedLicenses: string[];
  allLicenses: LicenseShort[];
}

interface DeleteUserDialogState {
  open: boolean;
  loading: boolean;
  user: User | null;
  deleteData: boolean;
  toMoveUser: string | undefined;
  tenantUsers: OptionType[];
}

const initialCreateAndEditState: CreateAndEditUserDialogState = {
  open: false,
  loading: false,
  user: null,
  firstName: '',
  lastName: '',
  email: '',
  role: Roles.BASIC,
  language: 'de',
  emailInvalid: false,
  emailAlreadyExists: false,
  firstNameInvalid: false,
  lastNameInvalid: false,
  invitingUserInvalid: false,
};

const initialAddLicenseState: AddLicenseToUserDialogState = {
  open: false,
  loading: false,
  licensesLoading: false,
  accountId: null,
  tenantId: null,
  allLicenses: [],
  selectedLicenses: [],
};

const initialManageUserLicensesState: ManageUserLicensesDialogState = {
  open: false,
  loading: false,
  licensesLoading: false,
  accountId: null,
  tenantId: null,
  selectedLicenses: [],
  allLicenses: [],
};

const initialDeleteUserState: DeleteUserDialogState = {
  open: false,
  loading: false,
  user: null,
  deleteData: true,
  toMoveUser: undefined,
  tenantUsers: [],
};

export function useUserDialogs() {
  const [createAndEditUserDialog, setCreateAndEditUserDialog] = useState<CreateAndEditUserDialogState>(initialCreateAndEditState);
  const [addLicenseToUserDialog, setAddLicenseToUserDialog] = useState<AddLicenseToUserDialogState>(initialAddLicenseState);
  const [manageUserLicensesDialog, setManageUserLicensesDialog] = useState<ManageUserLicensesDialogState>(initialManageUserLicensesState);
  const [deleteUserDialog, setDeleteUserDialog] = useState<DeleteUserDialogState>(initialDeleteUserState);

  // Create/Edit dialog handlers
  const resetCreateAndEditUserDialog = useCallback(() => {
    setCreateAndEditUserDialog(initialCreateAndEditState);
  }, []);

  // Add license to user dialog handlers
  const resetAddLicenseToUserDialog = useCallback(() => {
    setAddLicenseToUserDialog(initialAddLicenseState);
  }, []);

  // Manage user licenses dialog handlers
  const resetManageUserLicensesDialog = useCallback(() => {
    setManageUserLicensesDialog(initialManageUserLicensesState);
  }, []);

  // Delete user dialog handlers
  const resetDeleteUserDialog = useCallback(() => {
    setDeleteUserDialog(initialDeleteUserState);
  }, []);

  return {
    // States and setters (for direct state manipulation in handlers)
    createAndEditUserDialog,
    setCreateAndEditUserDialog,
    resetCreateAndEditUserDialog,

    addLicenseToUserDialog,
    setAddLicenseToUserDialog,
    resetAddLicenseToUserDialog,

    manageUserLicensesDialog,
    setManageUserLicensesDialog,
    resetManageUserLicensesDialog,

    deleteUserDialog,
    setDeleteUserDialog,
    resetDeleteUserDialog,
  };
}
